import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Icon,
  useColorMode,
  Divider,
} from '@chakra-ui/react';
import { Shield, ShieldOff, Calendar, FileText, CheckSquare, Square } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { partyServiceBlock } from '../../../../../redux/actions/salesInvoice';

/**
 * SerialNoBlockModal
 * Props:
 *  - isOpen        {boolean}
 *  - onClose       {function}
 *  - serialList    {Array}  – list of stock/serial objects: { id, itemName, serialNo, ... }
 *  - accountId     {number}
 *  - onSuccess     {function} – called after successful block
 */
const SerialNoBlockModal = ({ isOpen, onClose, serialList = [], accountId, onSuccess }) => {
  const dispatch = useDispatch();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const today = new Date().toISOString().split('T')[0];

  const [selectedIds, setSelectedIds] = useState([]);
  const [reason, setReason] = useState('');
  const [blockDate, setBlockDate] = useState(today);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedIdx, setFocusedIdx] = useState(null);

  // unique list by serialNo
  const uniqueList = useMemo(() => {
    const seen = new Set();
    return serialList.filter((s) => {
      if (!s.serialNo || seen.has(s.serialNo)) return false;
      seen.add(s.serialNo);
      return true;
    });
  }, [serialList]);

  /* ── selection helpers ── */
  const allSelected = uniqueList.length > 0 && selectedIds.length === uniqueList.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < uniqueList.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(uniqueList.map((s) => s.id));
    }
  };

  const toggleRow = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey) ? prev.filter((k) => k !== rowKey) : [...prev, rowKey],
    );
  };

  /* ── validation ── */
  const validate = () => {
    const newErrors = {};
    if (selectedIds.length === 0) newErrors.selected = 'Please select at least one serial number.';
    if (!blockDate) newErrors.blockDate = 'Block date is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ── submit ── */
  const handleSave = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      const payload = {
        accountId: accountId,
        salesIds: selectedIds,
        isBlocked: true,
        type: 'PURCHASE_RETURN',
        reason: reason.trim() || null,
        blockedAt: blockDate,
      };
      const res = await dispatch(partyServiceBlock(payload));
      if (res && res.code === 1) {
        handleClose();
        onSuccess && onSuccess();
      }
    } finally {
      setIsLoading(false);
    }
  };

  /* ── reset on close ── */
  const handleClose = () => {
    setSelectedIds([]);
    setReason('');
    setBlockDate(today);
    setErrors({});
    onClose();
  };

  /* ── colours ── */
  const bg = isDark ? 'gray.900' : 'white';
  const headerBg = isDark ? 'gray.800' : 'gray.50';
  const rowHover = isDark ? 'gray.700' : 'gray.50';
  const selectedRowBg = isDark ? 'blue.900' : 'blue.50';
  const borderColor = isDark ? 'gray.700' : 'gray.200';
  const textMuted = isDark ? 'gray.400' : 'gray.500';
  const inputBg = isDark ? 'gray.700' : 'white';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg={bg} borderRadius="2xl" boxShadow="2xl" overflow="hidden" mx={4}>

        {/* ── Gradient Header ── */}
        <Box
          bgGradient={
            isDark
              ? 'linear(to-r, red.700, orange.600)'
              : 'linear(to-r, red.500, orange.400)'
          }
          px={6}
          py={5}>
          <HStack spacing={4} align="center">
            <Box
              bg="whiteAlpha.200"
              p={2.5}
              borderRadius="xl"
              backdropFilter="blur(10px)">
              <Shield size={24} color="white" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold" color="white">
                Block Serial Numbers
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800">
                Select serial numbers, set a reason and block date
              </Text>
            </VStack>
          </HStack>
          <ModalCloseButton color="white" top={4} right={4} _hover={{ bg: 'whiteAlpha.200' }} />
        </Box>

        <ModalBody p={6}>
          <VStack spacing={5} align="stretch">

            {/* ── Serial Number Table ── */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontWeight="semibold" fontSize="sm" color={textMuted} textTransform="uppercase" letterSpacing="wide">
                  Serial Numbers
                </Text>
                {uniqueList.length > 0 && (
                  <Badge
                    colorScheme={selectedIds.length > 0 ? 'orange' : 'gray'}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="bold">
                    {selectedIds.length} / {uniqueList.length} selected
                  </Badge>
                )}
              </Flex>

              {uniqueList.length === 0 ? (
                <Box
                  border="2px dashed"
                  borderColor={borderColor}
                  borderRadius="xl"
                  py={10}
                  textAlign="center">
                  <VStack spacing={2}>
                    <ShieldOff size={36} color={isDark ? '#718096' : '#A0AEC0'} />
                    <Text color={textMuted} fontSize="sm">No serial numbers available</Text>
                  </VStack>
                </Box>
              ) : (
                <Box
                  border="2px solid"
                  borderColor={errors.selected ? 'red.400' : borderColor}
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="sm">
                  <TableContainer maxH="280px" overflowY="auto"
                    css={{
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-track': { background: isDark ? '#2D3748' : '#F7FAFC', borderRadius: '10px' },
                      '&::-webkit-scrollbar-thumb': { background: isDark ? '#E53E3E' : '#FC8181', borderRadius: '10px' },
                    }}>
                    <Table variant="simple" size="sm">
                      <Thead
                        position="sticky"
                        top={0}
                        bg={isDark ? 'gray.800' : 'gray.100'}
                        zIndex={10}
                        borderBottom="2px solid"
                        borderColor={isDark ? 'red.700' : 'red.300'}>
                        <Tr>
                          <Th py={3} w="50px">
                            <Checkbox
                              isChecked={allSelected}
                              isIndeterminate={someSelected}
                              onChange={toggleSelectAll}
                              colorScheme="red"
                              size="md"
                            />
                          </Th>
                          <Th py={3} color={isDark ? 'red.300' : 'red.600'} fontSize="xs">
                            Item Name
                          </Th>
                          <Th py={3} color={isDark ? 'red.300' : 'red.600'} fontSize="xs">
                            Serial Number
                          </Th>
                          <Th py={3} color={isDark ? 'red.300' : 'red.600'} fontSize="xs">
                            Warehouse
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {uniqueList.map((item, idx) => {
                          const rowKey = item.id ?? item.serialNo;
                          const isSelected = selectedIds.includes(rowKey);
                          const isFocused = focusedIdx === idx;
                          return (
                            <Tr
                              key={idx}
                              tabIndex={0}
                              bg={isSelected ? selectedRowBg : 'transparent'}
                              outline={isFocused ? '2px solid' : 'none'}
                              outlineColor={isFocused ? (isDark ? 'red.400' : 'red.500') : 'transparent'}
                              outlineOffset="-2px"
                              _hover={{ bg: isSelected ? selectedRowBg : rowHover, cursor: 'pointer' }}
                              _focusVisible={{
                                outline: '2px solid',
                                outlineColor: isDark ? 'red.400' : 'red.500',
                                outlineOffset: '-2px',
                              }}
                              onClick={() => toggleRow(rowKey)}
                              onFocus={() => setFocusedIdx(idx)}
                              onBlur={() => setFocusedIdx(null)}
                              onKeyDown={(e) => {
                                if (e.key === ' ') {
                                  e.preventDefault(); // stop page scroll
                                  toggleRow(rowKey);
                                }
                              }}
                              transition="background 0.15s">
                              <Td py={2.5}>
                                <Checkbox
                                  isChecked={isSelected}
                                  onChange={() => toggleRow(rowKey)}
                                  colorScheme="red"
                                  size="md"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Td>
                              <Td py={2.5}>
                                <Text
                                  fontWeight="medium"
                                  fontSize="sm"
                                  color={isDark ? 'gray.200' : 'gray.800'}
                                  noOfLines={1}>
                                  {item.itemName || '—'}
                                </Text>
                              </Td>
                              <Td py={2.5}>
                                <Badge
                                  colorScheme={isSelected ? 'red' : 'purple'}
                                  borderRadius="lg"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="bold"
                                  letterSpacing="wide">
                                  {item.serialNo}
                                </Badge>
                              </Td>
                              <Td py={2.5}>
                                <Text fontSize="sm" color={textMuted}>
                                  {item.warehouseName || '—'}
                                </Text>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {errors.selected && (
                <Text color="red.400" fontSize="xs" mt={1.5} ml={1}>
                  {errors.selected}
                </Text>
              )}
            </Box>

            <Divider borderColor={borderColor} />

            {/* ── Block Date ── */}
            <FormControl isInvalid={!!errors.blockDate}>
              <FormLabel
                fontSize="sm"
                fontWeight="semibold"
                color={isDark ? 'gray.300' : 'gray.700'}
                mb={2}>
                <HStack spacing={2}>
                  <Calendar size={15} />
                  <Text>Block Date</Text>
                </HStack>
              </FormLabel>
              <Input
                type="date"
                value={blockDate}
                onChange={(e) => {
                  setBlockDate(e.target.value);
                  setErrors((prev) => ({ ...prev, blockDate: undefined }));
                }}
                bg={inputBg}
                border="2px solid"
                borderColor={errors.blockDate ? 'red.400' : isDark ? 'gray.600' : 'gray.300'}
                borderRadius="xl"
                size="md"
                _hover={{ borderColor: isDark ? 'red.400' : 'red.500' }}
                _focus={{
                  borderColor: 'red.500',
                  boxShadow: `0 0 0 3px ${isDark ? 'rgba(229,62,62,0.2)' : 'rgba(229,62,62,0.15)'}`,
                }}
              />
              <FormErrorMessage>{errors.blockDate}</FormErrorMessage>
            </FormControl>

            {/* ── Reason ── */}
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="semibold"
                color={isDark ? 'gray.300' : 'gray.700'}
                mb={2}>
                <HStack spacing={2}>
                  <FileText size={15} />
                  <Text>Reason for Blocking <Text as="span" color={textMuted} fontWeight="normal">(optional)</Text></Text>
                </HStack>
              </FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for blocking these serial numbers…"
                bg={inputBg}
                border="2px solid"
                borderColor={isDark ? 'gray.600' : 'gray.300'}
                borderRadius="xl"
                rows={3}
                resize="none"
                fontSize="sm"
                _hover={{ borderColor: isDark ? 'red.400' : 'red.500' }}
                _focus={{
                  borderColor: 'red.500',
                  boxShadow: `0 0 0 3px ${isDark ? 'rgba(229,62,62,0.2)' : 'rgba(229,62,62,0.15)'}`,
                }}
              />
            </FormControl>

            {/* ── Action Bar ── */}
            <Box
              bg={isDark ? 'gray.800' : 'gray.50'}
              borderRadius="xl"
              px={4}
              py={3}
              border="2px solid"
              borderColor={borderColor}>
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  {selectedIds.length > 0 ? (
                    <>
                      <Icon as={() => <CheckSquare size={18} color={isDark ? '#FC8181' : '#E53E3E'} />} />
                      <Text fontSize="sm" fontWeight="semibold" color={isDark ? 'red.300' : 'red.600'}>
                        {selectedIds.length} serial number{selectedIds.length !== 1 ? 's' : ''} will be blocked
                      </Text>
                    </>
                  ) : (
                    <>
                      <Icon as={() => <Square size={18} color={textMuted} />} />
                      <Text fontSize="sm" color={textMuted}>
                        No serials selected
                      </Text>
                    </>
                  )}
                </HStack>
                <HStack spacing={3}>
                  <Button
                    variant="ghost"
                    size="md"
                    colorScheme="gray"
                    borderRadius="xl"
                    fontWeight="semibold"
                    onClick={handleClose}
                    isDisabled={isLoading}>
                    Cancel
                  </Button>
                  <Button
                    size="md"
                    borderRadius="xl"
                    fontWeight="bold"
                    px={7}
                    isLoading={isLoading}
                    loadingText="Blocking..."
                    isDisabled={uniqueList.length === 0}
                    onClick={handleSave}
                    bgGradient={
                      isDark
                        ? 'linear(to-r, red.600, orange.500)'
                        : 'linear(to-r, red.500, orange.400)'
                    }
                    color="white"
                    _hover={{
                      bgGradient: isDark
                        ? 'linear(to-r, red.500, orange.400)'
                        : 'linear(to-r, red.600, orange.500)',
                      transform: 'translateY(-1px)',
                      boxShadow: 'lg',
                    }}
                    _active={{ transform: 'translateY(0)' }}
                    leftIcon={<Shield size={16} />}>
                    Block Serials
                  </Button>
                </HStack>
              </Flex>
            </Box>

          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SerialNoBlockModal;
